// components/AdminMessaging/MessageDetail.tsx
// Message detail view with response composer

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    fetchMessageById,
    fetchResponseByMessage,
    markMessageAsRead,
    archiveMessage,
    sendAdminResponse,
    validateResponse,
    formatRelativeTime,
    getFilePreviewUrl,
    revokePreviewUrl,
} from '../../services/adminMessaging';
import type { AdminIncomingMessage, AdminResponse } from '../../types';
import './AdminMessaging.css';

type ResponseType = 'text' | 'voice' | 'image' | 'mixed';

export const MessageDetail: React.FC = () => {
    const { messageId } = useParams<{ messageId: string }>();
    const navigate = useNavigate();

    const [message, setMessage] = useState<AdminIncomingMessage | null>(null);
    const [adminResponse, setAdminResponse] = useState<AdminResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sending, setSending] = useState(false);

    // Response composer state
    const [responseType, setResponseType] = useState<ResponseType>('text');
    const [textContent, setTextContent] = useState('');
    const [voiceFile, setVoiceFile] = useState<File | null>(null);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [imageCaption, setImageCaption] = useState('');
    const [voicePreviewUrl, setVoicePreviewUrl] = useState<string>('');
    const [imagePreviewUrl, setImagePreviewUrl] = useState<string>('');


    useEffect(() => {
        // Cleanup preview URLs on unmount
        return () => {
            if (voicePreviewUrl) revokePreviewUrl(voicePreviewUrl);
            if (imagePreviewUrl) revokePreviewUrl(imagePreviewUrl);
        };
    }, [voicePreviewUrl, imagePreviewUrl]);

    const loadMessage = useCallback(async () => {
        try {
            setLoading(true);
            setError('');

            if (!messageId) {
                throw new Error('Message ID is required');
            }

            const msg = await fetchMessageById(messageId);
            setMessage(msg);

            // Mark as read if not already
            if (!msg.readByAdminAt && msg.status === 'pending') {
                await markMessageAsRead(messageId);
                msg.readByAdminAt = new Date();
            }

            // Fetch admin response if message has been responded to
            if (msg.status === 'responded' && msg.responseId) {
                const response = await fetchResponseByMessage(msg);
                setAdminResponse(response);
            }
        } catch (err: any) {
            console.error('Failed to load message:', err);
            setError(err.message || 'Failed to load message');
        } finally {
            setLoading(false);
        }
    }, [messageId]);

    useEffect(() => {
        if (messageId) {
            loadMessage();
        }
    }, [messageId, loadMessage]);

    const handleVoiceFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setVoiceFile(file);
            if (voicePreviewUrl) revokePreviewUrl(voicePreviewUrl);
            setVoicePreviewUrl(getFilePreviewUrl(file));
        }
    };

    const handleImageFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setImageFile(file);
            if (imagePreviewUrl) revokePreviewUrl(imagePreviewUrl);
            setImagePreviewUrl(getFilePreviewUrl(file));
        }
    };

    const handleSendResponse = async () => {
        if (!message || !messageId) return;

        // Validate response
        const validation = validateResponse({
            responseType,
            textContent,
            voiceFile: voiceFile || undefined,
            imageFile: imageFile || undefined,
        });

        if (!validation.isValid) {
            alert(`Invalid response:\n\n${validation.errors.join('\n')}`);
            return;
        }

        try {
            setSending(true);

            await sendAdminResponse(messageId, {
                responseType,
                textContent: textContent || undefined,
                voiceFile: voiceFile || undefined,
                imageFile: imageFile || undefined,
                imageCaption: imageCaption || undefined,
            });

            alert('✅ Response sent successfully!');

            // Navigate back to inbox
            navigate('/admin-messaging');
        } catch (err: any) {
            console.error('Failed to send response:', err);
            alert(`❌ Failed to send response:\n\n${err.message}`);
        } finally {
            setSending(false);
        }
    };

    const handleArchive = async () => {
        if (!messageId || !message) return;

        if (!window.confirm('Archive this message? It will be moved to the archived section.')) {
            return;
        }

        try {
            await archiveMessage(messageId);
            alert('✅ Message archived');
            navigate('/admin-messaging');
        } catch (err: any) {
            alert(`❌ Failed to archive message:\n\n${err.message}`);
        }
    };

    const isValidResponse = (): boolean => {
        const validation = validateResponse({
            responseType,
            textContent,
            voiceFile: voiceFile || undefined,
            imageFile: imageFile || undefined,
        });
        return validation.isValid;
    };

    if (loading) {
        return (
            <div className="messaging-container">
                <div className="loading-state">
                    <div className="loading-spinner"></div>
                    <p>Loading message...</p>
                </div>
            </div>
        );
    }

    if (error || !message) {
        return (
            <div className="messaging-container">
                <div className="error-state">
                    <h3>⚠️ Error</h3>
                    <p>{error || 'Message not found'}</p>
                    <button onClick={() => navigate('/admin-messaging')} className="back-button">
                        ← Back to Inbox
                    </button>
                </div>
            </div>
        );
    }

    const canRespond = message.status === 'pending';

    return (
        <div className="messaging-container">
            <header className="messaging-header">
                <div className="header-content">
                    <button
                        onClick={() => navigate('/admin-messaging')}
                        className="back-button"
                    >
                        ← Back to Inbox
                    </button>
                    <div>
                        <h1>Message from {message.fromExplorerName}</h1>
                        <p className="header-subtitle">
                            {formatRelativeTime(message.sentAt)}
                        </p>
                    </div>
                    <div className="header-actions">
                        {canRespond && (
                            <button onClick={handleArchive} className="archive-button">
                                📁 Archive
                            </button>
                        )}
                    </div>
                </div>
            </header>

            <main className="message-detail-main">
                {/* Message Info */}
                <section className="message-info-section">
                    <div className="info-header">
                        <h2>Message Details</h2>
                        <div className="status-badges">
                            <span className={`status-badge ${message.status}`}>
                                {message.status === 'pending' ? '📨' :
                                 message.status === 'responded' ? '✅' : '📁'}
                                {' '}
                                {message.status.charAt(0).toUpperCase() + message.status.slice(1)}
                            </span>
                            {!message.moderationPassed && (
                                <span className="moderation-flag">
                                    ⚠️ Moderation Flag
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="info-grid">
                        <div className="info-item">
                            <label>From:</label>
                            <span>{message.fromExplorerName}</span>
                        </div>
                        <div className="info-item">
                            <label>Sent:</label>
                            <span>{new Date(message.sentAt).toLocaleString()}</span>
                        </div>
                        {message.readByAdminAt && (
                            <div className="info-item">
                                <label>Read:</label>
                                <span>{new Date(message.readByAdminAt).toLocaleString()}</span>
                            </div>
                        )}
                        {message.respondedAt && (
                            <div className="info-item">
                                <label>Responded:</label>
                                <span>{new Date(message.respondedAt).toLocaleString()}</span>
                            </div>
                        )}
                    </div>

                    {!message.moderationPassed && message.moderationIssues.length > 0 && (
                        <div className="moderation-issues">
                            <h3>⚠️ Moderation Issues:</h3>
                            <ul>
                                {message.moderationIssues.map((issue, idx) => (
                                    <li key={idx}>{issue}</li>
                                ))}
                            </ul>
                        </div>
                    )}

                    <div className="message-content-box">
                        <h3>Message:</h3>
                        <p className="message-text">{message.message}</p>
                    </div>
                </section>

                {/* Response Composer */}
                {canRespond ? (
                    <section className="response-composer-section">
                        <h2>Send Response</h2>

                        {/* Response Type Selector */}
                        <div className="response-type-selector">
                            <label>Response Type:</label>
                            <div className="type-buttons">
                                <button
                                    className={`type-button ${responseType === 'text' ? 'active' : ''}`}
                                    onClick={() => setResponseType('text')}
                                >
                                    💬 Text
                                </button>
                                <button
                                    className={`type-button ${responseType === 'voice' ? 'active' : ''}`}
                                    onClick={() => setResponseType('voice')}
                                >
                                    🎙️ Voice
                                </button>
                                <button
                                    className={`type-button ${responseType === 'image' ? 'active' : ''}`}
                                    onClick={() => setResponseType('image')}
                                >
                                    🖼️ Image
                                </button>
                                <button
                                    className={`type-button ${responseType === 'mixed' ? 'active' : ''}`}
                                    onClick={() => setResponseType('mixed')}
                                >
                                    🎨 Mixed
                                </button>
                            </div>
                        </div>

                        {/* Text Content */}
                        {(responseType === 'text' || responseType === 'mixed') && (
                            <div className="response-field">
                                <label>Text Message:</label>
                                <textarea
                                    className="text-input"
                                    placeholder="Type your response..."
                                    value={textContent}
                                    onChange={(e) => setTextContent(e.target.value)}
                                    rows={6}
                                    maxLength={5000}
                                />
                                <span className="char-count">
                                    {textContent.length} / 5000 characters
                                </span>
                            </div>
                        )}

                        {/* Voice Upload */}
                        {(responseType === 'voice' || responseType === 'mixed') && (
                            <div className="response-field">
                                <label>Voice Message:</label>
                                <input
                                    type="file"
                                    accept="audio/*"
                                    onChange={handleVoiceFileChange}
                                    className="file-input"
                                />
                                {voiceFile && (
                                    <div className="file-preview">
                                        <span>🎙️ {voiceFile.name}</span>
                                        <span className="file-size">
                                            {(voiceFile.size / 1024 / 1024).toFixed(2)} MB
                                        </span>
                                        {voicePreviewUrl && (
                                            <audio controls src={voicePreviewUrl} />
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Image Upload */}
                        {(responseType === 'image' || responseType === 'mixed') && (
                            <div className="response-field">
                                <label>Image:</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageFileChange}
                                    className="file-input"
                                />
                                {imageFile && (
                                    <div className="file-preview">
                                        <span>🖼️ {imageFile.name}</span>
                                        <span className="file-size">
                                            {(imageFile.size / 1024 / 1024).toFixed(2)} MB
                                        </span>
                                        {imagePreviewUrl && (
                                            <img
                                                src={imagePreviewUrl}
                                                alt="Preview"
                                                className="image-preview"
                                            />
                                        )}
                                    </div>
                                )}

                                <label className="caption-label">Image Caption (optional):</label>
                                <input
                                    type="text"
                                    className="caption-input"
                                    placeholder="Add a caption for your image..."
                                    value={imageCaption}
                                    onChange={(e) => setImageCaption(e.target.value)}
                                    maxLength={500}
                                />
                            </div>
                        )}

                        {/* Action Buttons */}
                        <div className="response-actions">
                            <button
                                className="send-button"
                                onClick={handleSendResponse}
                                disabled={sending || !isValidResponse()}
                            >
                                {sending ? (
                                    <>
                                        <span className="spinner-small"></span>
                                        Sending...
                                    </>
                                ) : (
                                    <>✉️ Send Response</>
                                )}
                            </button>
                            <button
                                className="cancel-button"
                                onClick={() => navigate('/admin-messaging')}
                                disabled={sending}
                            >
                                Cancel
                            </button>
                        </div>
                    </section>
                ) : (
                    <section className="already-responded-section">
                        {message.status === 'responded' && adminResponse ? (
                            <div className="response-display">
                                <div className="response-header">
                                    <h3>✅ Admin Response</h3>
                                    <span className="response-type-badge">
                                        {adminResponse.responseType === 'text' && '💬 Text'}
                                        {adminResponse.responseType === 'voice' && '🎙️ Voice'}
                                        {adminResponse.responseType === 'image' && '🖼️ Image'}
                                        {adminResponse.responseType === 'mixed' && '🎨 Mixed'}
                                    </span>
                                </div>

                                <div className="response-metadata">
                                    <p><strong>Sent by:</strong> {adminResponse.sentByAdminEmail}</p>
                                    <p><strong>Sent at:</strong> {new Date(adminResponse.sentAt).toLocaleString()}</p>
                                </div>

                                <div className="response-content">
                                    {/* Text Content */}
                                    {adminResponse.textContent && (
                                        <div className="response-text-content">
                                            <h4>Message:</h4>
                                            <p className="response-text">{adminResponse.textContent}</p>
                                        </div>
                                    )}

                                    {/* Voice Content */}
                                    {adminResponse.voiceURL && (
                                        <div className="response-voice-content">
                                            <h4>Voice Message:</h4>
                                            <audio controls src={adminResponse.voiceURL} className="response-audio">
                                                Your browser does not support the audio element.
                                            </audio>
                                        </div>
                                    )}

                                    {/* Image Content */}
                                    {adminResponse.imageURL && (
                                        <div className="response-image-content">
                                            <h4>Image:</h4>
                                            <img
                                                src={adminResponse.imageURL}
                                                alt="Admin response"
                                                className="response-image"
                                            />
                                            {adminResponse.imageCaption && (
                                                <p className="image-caption">
                                                    <em>{adminResponse.imageCaption}</em>
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="info-box">
                                <h3>
                                    {message.status === 'responded' ? '✅ Already Responded' : '📁 Message Archived'}
                                </h3>
                                <p>
                                    {message.status === 'responded'
                                        ? 'This message has already been responded to.'
                                        : 'This message has been archived.'}
                                </p>
                                {message.respondedAt && (
                                    <p className="responded-time">
                                        Responded on {new Date(message.respondedAt).toLocaleString()}
                                    </p>
                                )}
                            </div>
                        )}
                    </section>
                )}
            </main>
        </div>
    );
};

export default MessageDetail;
